import dayjs from 'dayjs';
import { fetchApi } from '../api';
import { getGidFromIdForResource } from '../../../utils/gid-utils';
import { APIOrderProps, UIOrderProps, LineItem } from './types';

export const getLastOrderId = (): string | undefined => {
  if (typeof window === 'undefined') {
    return;
  }

  const billingAttempts = window.Awtomic?.subscriptionBillingAttempts || [];
  const originOrderId = window.Awtomic?.subscriptionDetails?.OriginOrderId;
  if (!billingAttempts.length && !originOrderId) {
    return;
  }

  let orderId;
  if (billingAttempts.length) {
    const lastBillingAttempt = billingAttempts
      .sort((a, b) => Date.parse(b.CreatedAt) - Date.parse(a.CreatedAt))
      .find((billingAttempt) => 'OrderId' in billingAttempt);

    if (lastBillingAttempt) {
      orderId = lastBillingAttempt.OrderId;
    }
  }

  if (!orderId && originOrderId) {
    orderId = originOrderId;
  }

  return orderId ? orderId.split('/').pop() : undefined;
};

export const getLastOrderDetails = async (
  orderId: string,
  authToken: string,
) => {
  try {
    const res = await fetchApi({
      url: `/orders/${orderId}`,
      authToken,
    });

    if (!res.ok) {
      throw new Error('Network response was not ok');
    }

    const orderData = await res.json();

    return orderData;
  } catch (error) {
    console.error(error);
  }
};

export const getResourcesImages = async (
  customerId: string,
  subscriptionId: string,
  resourcesIds: Array<string>,
  authToken: string,
): Promise<{ [key: string]: string | null }> => {
  try {
    const res = await fetchApi({
      url: `/customers/${customerId}/subscriptions/${subscriptionId}/productVariants/images`,
      method: 'POST',
      body: {
        productVariantsIds: resourcesIds,
      },
      authToken,
    });

    if (!res.ok) {
      throw new Error('Network response was not ok');
    }

    const resourcesData = await res.json();

    const images = {};
    for (const resource of resourcesData) {
      const { image = {}, product = {} } = resource;
      images[resource.gId] = image?.url || product?.featuredImage?.url;
    }

    return images;
  } catch (error) {
    console.error(error);
  }
};

export const formatOrderData = (orderData: APIOrderProps): UIOrderProps => {
  const {
    lineItems = [],
    shippingLines = [],
    currentSubtotalPriceSet: { presentment_money: subtotalPrice },
    currentTotalDiscountsSet: { presentment_money: totalDiscount },
    currentTotalPriceSet: { presentment_money: totalPrice },
    currentTotalTaxSet: { presentment_money: totalTax } = {},
    completedAt,
    createdAt,
    fulfillmentStatus,
  } = orderData;

  const resourcesIds = {};
  const sortedItems = lineItems.toSorted((a, b) =>
    a.quantity < b.quantity ? 1 : -1,
  );
  const items = sortedItems
    .filter((item: LineItem) => item.current_quantity > 0) // exclude refunded and removed items
    .map((item: LineItem) => {
      const {
        variant_id: variantId,
        quantity,
        price_set: {
          presentment_money: { amount: itemPrice, currency_code: itemCurrency },
        },
        discount_allocations: discounts = [],
        title: productTitle,
        variant_title: variantTitle,
      } = item;

      // This id will be used to fetch the images
      resourcesIds[getGidFromIdForResource(variantId, 'ProductVariant')] =
        quantity;

      // Each item can have several discounts, sum them up
      let totalDiscounts = 0;
      if (discounts.length) {
        totalDiscounts = discounts.reduce(
          (acc: number, discount) => acc + parseFloat(discount.amount),
          0,
        );
      }

      // Calculate item's total price after discounts
      const discountedPrice = parseFloat(itemPrice) * quantity - totalDiscounts;

      return {
        variantId,
        quantity,
        itemTotal: discountedPrice,
        itemCurrency,
        productTitle,
        variantTitle,
      };
    });

  // Calculate order's total shipping
  let totalShipping = 0;
  let shippingCurrency;
  for (const shippingLine of shippingLines) {
    const {
      discounted_price_set: {
        presentment_money: { amount, currency_code: currencyCode },
      },
    } = shippingLine;

    totalShipping += parseFloat(amount);
    shippingCurrency = currencyCode;
  }

  return {
    totalPrice: {
      amount: totalPrice.amount,
      currency: totalPrice.currency_code,
    },
    subtotalPrice: {
      amount: subtotalPrice.amount,
      currency: subtotalPrice.currency_code,
    },
    totalDiscount: {
      amount: totalDiscount.amount,
      currency: totalDiscount.currency_code,
    },
    totalTax: {
      amount: totalTax.amount,
      currency: totalTax.currency_code,
    },
    totalShipping: {
      amount: totalShipping.toString(),
      currency: shippingCurrency,
    },
    orderDate: dayjs(completedAt || createdAt).format('MMM DD, YYYY'),
    isFulfilled: fulfillmentStatus === 'fulfilled',
    items,
    resourcesIds,
  };
};
